import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Save,
  Search,
  Plane,
  Ship,
  Building,
  Warehouse,
  MapPin,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/lib/hooks/use-toast';
import {
  freightLocationsService,
  FREIGHT_LOCATION_TYPE_LABELS,
  type FreightLocation,
  type FreightLocationType,
  type CreateFreightLocationDto,
} from '../services/freight-locations-service';
import { LocationMapPicker } from './location-map-picker';

const TYPE_ICONS: Record<FreightLocationType, React.ElementType> = {
  AIRPORT: Plane,
  SEAPORT: Ship,
  INLAND_TERMINAL: Building,
  WAREHOUSE: Warehouse,
  CUSTOMS: Shield,
  OTHER: MapPin,
};

const TYPE_KEYS: FreightLocationType[] = [
  'AIRPORT',
  'SEAPORT',
  'INLAND_TERMINAL',
  'WAREHOUSE',
  'CUSTOMS',
  'OTHER',
];

export function FreightLocationTable() {
  const { toast } = useToast();
  const [locations, setLocations] = useState<FreightLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editing, setEditing] = useState<FreightLocation | null>(null);

  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const data = await freightLocationsService.list({
        q: searchQ || undefined,
        type: (typeFilter as FreightLocationType) || undefined,
      });
      setLocations(data);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, typeFilter]);

  const handleDelete = async (loc: FreightLocation) => {
    if (
      !(await confirmar({
        title: `¿Desactivar "${loc.name}"?`,
        variant: 'destructive',
        confirmText: 'Sí, desactivar',
      }))
    )
      return;
    try {
      await freightLocationsService.remove(loc.id);
      toast({ title: 'Localización desactivada', variant: 'success' });
      fetchLocations();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Buscar por código, nombre o ciudad..."
            className="pl-9"
          />
        </div>
        <Select
          value={typeFilter || '__all__'}
          onValueChange={(v) => setTypeFilter(v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los tipos</SelectItem>
            {TYPE_KEYS.map((t) => (
              <SelectItem key={t} value={t}>
                {FREIGHT_LOCATION_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={() => {
            setEditing(null);
            setShowFormDialog(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva localización
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : locations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchQ || typeFilter
            ? 'Sin resultados con esos filtros.'
            : 'No hay localizaciones. Crea una para empezar.'}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Ciudad</TableHead>
              <TableHead>País</TableHead>
              <TableHead className="text-center">Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations.map((loc) => {
              const Icon = TYPE_ICONS[loc.type];
              return (
                <TableRow key={loc.id}>
                  <TableCell className="font-mono text-xs">{loc.code}</TableCell>
                  <TableCell className="font-medium">{loc.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="gap-1">
                      <Icon className="w-3 h-3" />
                      {FREIGHT_LOCATION_TYPE_LABELS[loc.type]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{loc.city || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{loc.countryCode || '—'}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant={loc.isActive ? 'default' : 'secondary'}>
                      {loc.isActive ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditing(loc);
                          setShowFormDialog(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(loc)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <FreightLocationFormDialog
        open={showFormDialog}
        onOpenChange={setShowFormDialog}
        location={editing}
        onSaved={fetchLocations}
      />
    </div>
  );
}

interface FormState {
  name: string;
  type: FreightLocationType;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  latitude: string;
  longitude: string;
  timeZone: string;
  phone: string;
  email: string;
  website: string;
  contactName: string;
  iataCode: string;
  unLocode: string;
  notes: string;
  sapCode: string;
}

const EMPTY: FormState = {
  name: '',
  type: 'OTHER',
  address: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: '',
  latitude: '',
  longitude: '',
  timeZone: '',
  phone: '',
  email: '',
  website: '',
  contactName: '',
  iataCode: '',
  unLocode: '',
  notes: '',
  sapCode: '',
};

function FreightLocationFormDialog({
  open,
  onOpenChange,
  location,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: FreightLocation | null;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (location) {
      setForm({
        name: location.name,
        type: location.type,
        address: location.address || '',
        city: location.city || '',
        state: location.state || '',
        postalCode: location.postalCode || '',
        countryCode: location.countryCode || '',
        latitude: location.latitude != null ? String(location.latitude) : '',
        longitude: location.longitude != null ? String(location.longitude) : '',
        timeZone: location.timeZone || '',
        phone: location.phone || '',
        email: location.email || '',
        website: location.website || '',
        contactName: location.contactName || '',
        iataCode: location.iataCode || '',
        unLocode: location.unLocode || '',
        notes: location.notes || '',
        sapCode: (location as any).sapCode || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [location, open]);

  const setField = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const lat = form.latitude ? parseFloat(form.latitude) : null;
  const lng = form.longitude ? parseFloat(form.longitude) : null;

  const handleMapPick = (newLat: number, newLng: number) => {
    setForm((f) => ({ ...f, latitude: String(newLat), longitude: String(newLng) }));
  };

  // Geocodificar usando Nominatim (OpenStreetMap, gratis, sin API key)
  const handleGeocode = async () => {
    const query = [form.address, form.city, form.state, form.countryCode]
      .filter(Boolean)
      .join(', ')
      .trim();
    if (!query) {
      toast({
        title: 'Falta dirección',
        description: 'Llená al menos ciudad o dirección',
        variant: 'destructive',
      });
      return;
    }
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
        {
          headers: { 'Accept-Language': 'es' },
        }
      );
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const { lat: foundLat, lon: foundLng } = data[0];
        setForm((f) => ({ ...f, latitude: foundLat, longitude: foundLng }));
        toast({ title: 'Coordenadas encontradas', variant: 'success' });
      } else {
        toast({ title: 'No se encontró la dirección', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error al geocodificar', variant: 'destructive' });
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const dto: CreateFreightLocationDto = {
        name: form.name.trim(),
        type: form.type,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        postalCode: form.postalCode.trim() || null,
        countryCode: form.countryCode.trim().toUpperCase() || null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        timeZone: form.timeZone.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        website: form.website.trim() || null,
        contactName: form.contactName.trim() || null,
        iataCode: form.iataCode.trim().toUpperCase() || null,
        unLocode: form.unLocode.trim().toUpperCase() || null,
        notes: form.notes.trim() || null,
        sapCode: form.sapCode.trim() || null,
      } as any;
      if (location) {
        await freightLocationsService.update(location.id, dto);
        toast({ title: 'Localización actualizada', variant: 'success' });
      } else {
        await freightLocationsService.create(dto);
        toast({ title: 'Localización creada', variant: 'success' });
      }
      onOpenChange(false);
      onSaved();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{location ? 'Editar localización' : 'Nueva localización'}</DialogTitle>
          <DialogDescription>
            Aeropuertos, puertos, terminales, almacenes o aduanas que se usarán como origen/destino
            de tramos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {location && (
            <div className="text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
              Código:{' '}
              <span className="font-mono font-semibold text-foreground">{location.code}</span>
              <span className="ml-2 italic">(autogenerado, no editable)</span>
            </div>
          )}

          {/* — Identidad — */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Identidad
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setField('type', v as FreightLocationType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_KEYS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {FREIGHT_LOCATION_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Nombre</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Aeropuerto Internacional Miami"
                  required
                />
              </div>
            </div>
            {(form.type === 'AIRPORT' || form.type === 'SEAPORT') && (
              <div className="grid grid-cols-2 gap-3">
                {form.type === 'AIRPORT' && (
                  <div>
                    <Label>Código IATA</Label>
                    <Input
                      value={form.iataCode}
                      onChange={(e) => setField('iataCode', e.target.value.toUpperCase())}
                      placeholder="MIA, VVI, JFK..."
                      maxLength={3}
                    />
                  </div>
                )}
                {form.type === 'SEAPORT' && (
                  <div>
                    <Label>UN/LOCODE</Label>
                    <Input
                      value={form.unLocode}
                      onChange={(e) => setField('unLocode', e.target.value.toUpperCase())}
                      placeholder="USMIA, BOLPB..."
                      maxLength={5}
                    />
                  </div>
                )}
              </div>
            )}
          </section>

          {/* — Dirección — */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Dirección
            </h4>
            <div>
              <Label>Calle / Dirección</Label>
              <Input
                value={form.address}
                onChange={(e) => setField('address', e.target.value)}
                placeholder="Av. Ejemplo 123"
              />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label>Ciudad</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                  placeholder="Miami"
                />
              </div>
              <div>
                <Label>Estado / Provincia</Label>
                <Input
                  value={form.state}
                  onChange={(e) => setField('state', e.target.value)}
                  placeholder="FL, Santa Cruz..."
                />
              </div>
              <div>
                <Label>Código postal</Label>
                <Input
                  value={form.postalCode}
                  onChange={(e) => setField('postalCode', e.target.value)}
                  placeholder="33126"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>País (ISO 2)</Label>
                <Input
                  value={form.countryCode}
                  onChange={(e) => setField('countryCode', e.target.value.toUpperCase())}
                  placeholder="US, BO..."
                  maxLength={2}
                />
              </div>
              <div className="col-span-2">
                <Label>Zona horaria</Label>
                <Input
                  value={form.timeZone}
                  onChange={(e) => setField('timeZone', e.target.value)}
                  placeholder="America/La_Paz, America/New_York..."
                />
              </div>
            </div>
          </section>

          {/* — Mapa — */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Ubicación en el mapa
              </h4>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleGeocode}
                disabled={geocoding}
              >
                {geocoding ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Search className="w-3.5 h-3.5 mr-1" />
                )}
                Buscar por dirección
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Click en el mapa para fijar la posición, o arrastrá el marcador. También podés usar
              "Buscar por dirección" para auto-completar las coordenadas desde la ciudad/dirección.
            </p>
            <LocationMapPicker latitude={lat} longitude={lng} onChange={handleMapPick} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Latitud</Label>
                <Input
                  type="number"
                  step="0.0000001"
                  value={form.latitude}
                  onChange={(e) => setField('latitude', e.target.value)}
                  placeholder="-16.5"
                />
              </div>
              <div>
                <Label>Longitud</Label>
                <Input
                  type="number"
                  step="0.0000001"
                  value={form.longitude}
                  onChange={(e) => setField('longitude', e.target.value)}
                  placeholder="-68.15"
                />
              </div>
            </div>
          </section>

          {/* — Contacto — */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Contacto
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Persona de contacto</Label>
                <Input
                  value={form.contactName}
                  onChange={(e) => setField('contactName', e.target.value)}
                  placeholder="Juan Pérez"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  placeholder="+591 3 1234567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder="contacto@ejemplo.com"
                />
              </div>
              <div>
                <Label>Sitio web</Label>
                <Input
                  value={form.website}
                  onChange={(e) => setField('website', e.target.value)}
                  placeholder="https://ejemplo.com"
                />
              </div>
            </div>
          </section>

          {/* — Notas y código SAP — */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Notas</Label>
              <Input
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Observaciones (opcional)"
              />
            </div>
            <div>
              <Label>Código SAP</Label>
              <Input
                value={form.sapCode}
                onChange={(e) => setField('sapCode', e.target.value)}
                placeholder="Código equivalente en SAP (opcional)"
                maxLength={64}
              />
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {location ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
